/** Shared avatar upload constraints — enforced identically by the client pre-check and the
 *  server-side validation, so they can't silently drift apart. */
export const AVATAR_MAX_BYTES = 5 * 1024 * 1024;
export const AVATAR_ALLOWED_MIME_TYPES = ['image/png', 'image/jpeg'];

export interface AvatarValidationResult {
  valid: boolean;
  errors: string[];
}

/** Validate avatar file size against the 5MB limit shown in the account settings UI. */
export function validateAvatarFileSize(fileSizeBytes: number): AvatarValidationResult {
  const errors: string[] = [];

  if (fileSizeBytes <= 0) {
    errors.push('File size must be greater than 0');
  }

  if (fileSizeBytes > AVATAR_MAX_BYTES) {
    errors.push(`File size exceeds maximum allowed size of ${AVATAR_MAX_BYTES / (1024 * 1024)}MB`);
  }

  return {valid: errors.length === 0, errors};
}

/** Validate avatar MIME type — PNG/JPEG only, narrower than the general document upload allowlist. */
export function validateAvatarMimeType(mimeType: string): AvatarValidationResult {
  const errors: string[] = [];

  if (!AVATAR_ALLOWED_MIME_TYPES.includes(mimeType)) {
    errors.push(
      `MIME type '${mimeType}' is not allowed. Allowed types: ${AVATAR_ALLOWED_MIME_TYPES.join(', ')}`
    );
  }

  return {valid: errors.length === 0, errors};
}

/** Document upload size limit — enforced by the client pre-check and the API's server-side validation. */
export const FILE_UPLOAD_MAX_SIZE_BYTES = 10 * 1024 * 1024;

/**
 * Formats a file size in bytes to a human-readable string.
 * e.g. 1234 → "1.2 KB", 1234567 → "1.2 MB"
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, i);
  return `${value.toFixed(value < 10 ? 1 : 0)} ${units[i]}`;
}

/**
 * Extracts the uppercased file extension from a filename.
 * e.g. "document.pdf" → "PDF"
 */
export function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf('.');
  if (dotIndex <= 0) return '';
  return fileName.slice(dotIndex + 1).toUpperCase();
}
